import React, { useState, useEffect } from 'react';
import './PartnerDashboard.css';
import { useAuth } from '../../../components/contexts/AuthContext';
import { toast } from 'sonner';
import { FaToggleOn, FaToggleOff, FaCheckCircle, FaTimesCircle, FaFolder, FaTrashAlt } from 'react-icons/fa';
import RAGKeyAnalytics from '../../../utils/ragKeyAnalytics';
// import Chat from './chat';

const styles = {
  modalOverlay: {
    position: "fixed",
    top: 1,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    // alignItems: "center",
    zIndex: 9999
  },

  modalContent: {
    marginTop: "5vh",
    background: "#fff",
    padding: "20px",
    borderRadius: "10px",
    width: "80%",
    maxHeight: "80vh",
    overflowY: "auto",
    position: "relative"
  },

  closeBtn: {
    position: "absolute",
    top: "10px",
    right: "10px",
    border: "none",
    background: "red",
    color: "#fff",
    padding: "5px 10px",
    cursor: "pointer",
    borderRadius: "5px"
  }
};

const PartnerDashboard = () => {
  const [activeTab, setActiveTab] = useState('table-view');
  const [companyName, setCompanyName] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [files, setFiles] = useState([]);
  const [fileUrls, setFileUrls] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedKey, setGeneratedKey] = useState('');
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [loadingStage, setLoadingStage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [userData, setUserData] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Document Management states
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileDocuments, setProfileDocuments] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [newFileUrls, setNewFileUrls] = useState({});
  const [isManaging, setIsManaging] = useState(false);
  const [managementOperation, setManagementOperation] = useState('');
  const [selectedKey, setSelectedKey] = useState(null);
  const [activeCompany, setActiveCompany] = useState(null);
  const [activeDepartment, setActiveDepartment] = useState(null);
  // 🔒 SECURITY: Get actual user data from authentication context
  const { user } = useAuth();
  console.log(user, 'user--dsa')
  const userRole = user?.role || 'user'; // Get role from authenticated user
  const currentUserId = user?._id || user?.userId || user?.id; // Get user ID from authenticated user
  console.log('🔐 Current User ID:', currentUserId);
  console.log('🔐 Authentication Check:', { userRole, currentUserId, fullUser: user });
  const authToken = localStorage.getItem('rag_token');
    console.log('🔐 Auth token exists:', authToken);
  // Fetch partners data from RAG backend on component mount
  useEffect(() => {
    // ✅ Now using real authentication data from AuthContext
    // - userRole: 'admin' (sees all entries in admin panel)
    // - userRole: 'partner' (sees only own entries - security enforced)
    fetchPartners();
  }, [userRole, currentUserId]);

  const handleRagKeyClick = (user) => {
    setSelectedKey(user.uniqueKey);
    setActiveCompany(user.companyName); // Set the active company for analytics
    setActiveDepartment(user.departmentName); // Set the active department for analytics
  }

  const fetchPartners = async () => {
    try {
      setIsLoadingData(true);

      console.log('🔐 Full Authentication Debug:', {
        user,
        userRole,
        currentUserId,
        localStorage: localStorage.getItem('user')
      });

      // 🔒 SECURITY: Check for authentication - with better debugging
      if (!user && !localStorage.getItem('user')) {
        console.error('🚫 No authenticated user found in context or localStorage');
        toast.error('Please log in to access the dashboard');
        return;
      }

      // If user is not in context, try to get from localStorage
      let authenticatedUser = user;
      if (!authenticatedUser) {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        authenticatedUser = storedUser;
        console.log('🔄 Retrieved user from localStorage:', storedUser);
      }

      const finalUserId = authenticatedUser?._id || authenticatedUser?.userId || authenticatedUser?.id;
      const finalUserRole = authenticatedUser?.role || 'user';

      if (!finalUserId) {
        console.error('🚫 No user ID found in authentication data');
        console.log('Available user data:', authenticatedUser);
        toast.error('Authentication error: User ID not found. Please log in again.');
        return;
      }

      console.log(`🔐 Final Authentication: Role=${finalUserRole}, UserID=${finalUserId}`);

      // SECURITY: Build URL based on user role
      let apiUrl;

      if (finalUserRole === 'admin') {
        // Admin can see all entries (admin panel)
        apiUrl = `${process.env.REACT_APP_RAG_SERVER_URL}/rag-api/users/admin/entries`;
        console.log('👑 Admin Panel: Fetching all entries');
      } else {
        // 🔒 SECURITY: Non-admin users (partner/user) can ONLY see their own entries
        apiUrl = `${process.env.REACT_APP_RAG_SERVER_URL}/rag-api/users/partner/entries/${finalUserId}`;
        console.log(`🔒 User Panel: Fetching only entries for user ${finalUserId} (role: ${finalUserRole})`);
      }

      console.log(`🌐 API URL: ${apiUrl}`);

      const response = await fetch(apiUrl,{
                            method: "GET",
                            headers: {
                                "Authorization": `Bearer ${authToken}`,
                                // "Content-Type": "application/json"
                            }
                        },{ credentials: 'include' }, { credentials: 'include' });
      const result = await response.json();

      console.log('📊 API Response:', result);

      if (result.success) {
        // Transform MongoDB data to frontend format
        let transformedData = [];

        if (result.data && result.data.length > 0) {
          transformedData = result.data.map((entry, index) => ({
            id: entry._id || index + 1,
            userName: entry.userName || 'Unknown User', // Use actual userName from backend
            companyName: entry.companyName || 'N/A',
            departmentName: entry.departmentName || 'N/A',
            uniqueKey: entry.uniqueKey || 'N/A', // This is the key we need for deletion
            files: entry.files ? entry.files.map(f => f.originalName || f.fileName || f.name) : [],
            createdAt: entry.createdAt ? new Date(entry.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            userId: entry.userId,
            filesUploaded: entry.filesUploaded || (entry.files ? entry.files.length : 0),
            isActive: entry.isActive !== undefined ? entry.isActive : true, // Add isActive field
            lastDocumentActivity: entry.lastDocumentActivity || null // Track document add/delete activity
          }));
        }

        // SECURITY LOG: Show what data was received
        console.log(`🔒 Security: Received ${transformedData.length} entries for role '${finalUserRole}'`);
        console.log('🔍 Transformed data with uniqueKeys and status:', transformedData.map(d => ({
          companyName: d.companyName,
          uniqueKey: d.uniqueKey,
          isActive: d.isActive
        })));
        if (finalUserRole !== 'admin') {
          console.log(`🔒 Security: Non-admin user can only see their own entries (userId: ${finalUserId})`);
        }

        setUserData(transformedData);
      } else {
        console.error('Failed to fetch partners:', result.message);
        toast.error('Failed to load partner data');
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast.error('Error loading partner data. Please make sure the RAG backend server is running on port 5000.');
    } finally {
      setIsLoadingData(false);
    }
  };

  // Handle body class for loading modal
  useEffect(() => {
    if (isLoading) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'unset';
    };
  }, [isLoading]);

  // Handle body class for key modal
  useEffect(() => {
    if (showKeyModal) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'unset';
    };
  }, [showKeyModal]);

  // Handle escape key to close modals
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.keyCode === 27) {
        if (showKeyModal) {
          setShowKeyModal(false);
        }
        // Note: isLoading modal cannot be closed with escape as it's a process indicator
      }
    };

    if (showKeyModal) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showKeyModal]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(file =>
      file.type === 'application/pdf' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/msword'
    );
    setFiles(prev => [...prev, ...validFiles]);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file =>
      file.type === 'application/pdf' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/msword'
    );
    setFiles(prev => [...prev, ...validFiles]);
  };

  const handleUrlChange = (fileName, url) => {
    setFileUrls(prev => ({
      ...prev,
      [fileName]: url
    }));
  };

  const removeFile = (index) => {
    const fileToRemove = files[index];
    setFiles(files.filter((_, i) => i !== index));
    // Also remove the URL for this file
    setFileUrls(prev => {
      const updated = { ...prev };
      delete updated[fileToRemove.name];
      return updated;
    });
  };

  const handleCreateProfile = async () => {
    if (!companyName || !departmentName || files.length === 0) {
      toast.warning('Please fill in all fields and upload at least one document');
      return;
    }

    setIsLoading(true);
    setLoadingStage('Uploading documents...');

    console.log('Creating profile with:', {
      companyName,
      departmentName,
      files: files.map(f => f.name),
      fileUrls
    });

    try {
      // Create FormData for multipart/form-data request
      const formData = new FormData();
      formData.append('companyName', companyName);
      formData.append('departmentName', departmentName);
      formData.append('referenceUrls', JSON.stringify(fileUrls));
      formData.append('user_id', currentUserId); // Add user_id to the request

      // Append all PDF files
      files.forEach(file => {
        formData.append('pdfs', file);
      });

      setLoadingStage('Processing documents and building knowledge base...');

      // Make API call to RAG backend
      console.log('Sending request to RAG backend');
      const response = await fetch(`${process.env.REACT_APP_RAG_SERVER_URL}/rag-api/users/create-entry`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          "Authorization": `Bearer ${authToken}`,
        },
        body: formData
      });
      console.log('Response received from RAG backend', response);
      const result = await response.json();

      if (result.success) {
        setLoadingStage('Profile created successfully!');
        setGeneratedKey(result.data.uniqueKey);
        setShowKeyModal(true);

        // Refresh the partners list
        await fetchPartners();

        // Reset form
        setCompanyName('');
        setDepartmentName('');
        setFiles([]);
        setFileUrls({});

        // Go back to table view after successful creation
        setTimeout(() => {
          setActiveTab('table-view');
        }, 2000);
        setFileUrls({});
      } else {
        toast.error(`Error: ${result.message}`);
      }

    } catch (error) {
      console.log('Error creating profile:', error);
      toast.error('Error creating profile. Please make sure the RAG backend server is running on port 5000.');
    } finally {
      setIsLoading(false);
      setLoadingStage('');
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(generatedKey);
    toast.success('Key copied to clipboard!');
  };

  const handleCloseModal = () => {
    setShowKeyModal(false);
    setGeneratedKey('');
  };

  // Filter data based on search term
  const filteredUserData = userData.filter(user =>
    user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.departmentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.uniqueKey.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateNew = () => {
    setActiveTab('admin-setup');
  };

  const handleBackToTable = () => {
    setActiveTab('table-view');
  };

  const handleDeletePartner = async (partnerKey) => {
    if (window.confirm('Are you sure you want to delete this partner profile? This action cannot be undone.')) {
      try {
        console.log(`🗑️ Attempting to delete partner with key: ${partnerKey}`);

        const response = await fetch(`${process.env.REACT_APP_RAG_SERVER_URL}/rag-api/users/entry/${partnerKey}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            "Authorization": `Bearer ${authToken}`,
          }
        });

        const result = await response.json();
        console.log('Delete response:', result);

        if (result.success) {
          console.log('✅ Partner deleted successfully:', result);
          toast.success('Partner deleted successfully');
          await fetchPartners(); // Refresh the list
        } else {
          console.error('❌ Delete failed:', result);
          toast.error(`Error: ${result.message}`);
        }
      } catch (error) {
        console.error('❌ Error deleting partner:', error);
        toast.error('Error deleting partner. Please try again.');
      }
    }
  };

  const handleToggleStatus = async (partnerKey, currentStatus) => {
    try {
      console.log(`🔄 Toggling status for key: ${partnerKey}, current status: ${currentStatus}`);

      const response = await fetch(`${process.env.REACT_APP_RAG_SERVER_URL}/rag-api/users/entry/${partnerKey}/toggle-status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${authToken}`,
        },
      });

      const result = await response.json();
      console.log('Toggle status response:', result);

      if (result.success) {
        console.log('✅ Status toggled successfully:', result);
        toast.success(`Entry ${result.data.isActive ? 'activated' : 'deactivated'} successfully`);
        // Refresh the partners list to show updated status
        await fetchPartners();
      } else {
        console.error('❌ Toggle status failed:', result);
        toast.error(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error('❌ Error toggling status:', error);
      toast.error(`Error changing status: ${error.message}. Please try again.`);
    }
  };

  // Document Management Functions
  const handleManageDocuments = async (profile) => {
    setSelectedProfile(profile);
    setActiveTab('manage-documents');
    await fetchDocuments(profile.uniqueKey);
  };

  const fetchDocuments = async (key) => {
    try {
      setIsLoadingDocuments(true);
      console.log(`🔍 Fetching documents for key: ${key}`);

      const response = await fetch(`${process.env.REACT_APP_RAG_SERVER_URL}/rag-api/users/documents/${key}`, {
        credentials: 'include',
        headers: {
          "Authorization": `Bearer ${authToken}`,
        }
      });
      console.log(`📡 Response status: ${response.status}`);

      const result = await response.json();
      console.log(`📊 API Response:`, result);

      if (result.success) {
        setProfileDocuments(result.data.documents || []);
        console.log(`✅ Successfully loaded ${result.data.documents?.length || 0} documents`);
      } else {
        console.error('❌ Failed to fetch documents:', result.message);
        toast.error(`Failed to load documents: ${result.message}`);
      }
    } catch (error) {
      console.error('❌ Error fetching documents:', error);
      toast.error(`Error loading documents: ${error.message}`);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleAddDocuments = async () => {
    if (!selectedProfile || newFiles.length === 0) {
      toast.warning('Please select files to upload');
      return;
    }

    try {
      setIsManaging(true);
      setManagementOperation('Adding documents...');

      const formData = new FormData();
      formData.append('referenceUrls', JSON.stringify(newFileUrls));

      newFiles.forEach(file => {
        formData.append('pdfs', file);
      });

      const response = await fetch(`${process.env.REACT_APP_RAG_SERVER_URL}/rag-api/users/documents/${selectedProfile.uniqueKey}/add`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          "Authorization": `Bearer ${authToken}`,
        },
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`Successfully added ${result.data.addedFiles} document(s)`);
        setNewFiles([]);
        setNewFileUrls({});
        await fetchDocuments(selectedProfile.uniqueKey);
        await fetchPartners(); // Refresh main list
      } else {
        toast.error(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error('Error adding documents:', error);
      toast.error('Error adding documents. Please try again.');
    } finally {
      setIsManaging(false);
      setManagementOperation('');
    }
  };

  const handleRemoveDocuments = async () => {
    if (selectedDocuments.length === 0) {
      toast.warning('Please select documents to remove');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${selectedDocuments.length} document(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsManaging(true);
      setManagementOperation('Removing documents...');

      const response = await fetch(`${process.env.REACT_APP_RAG_SERVER_URL}/rag-api/users/documents/${selectedProfile.uniqueKey}/remove`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          "Authorization": `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filenames: selectedDocuments })
      });

      const result = await response.json();

      if (result.success) {
        const { removed, missing } = result.data;
        let message = '';
        if (removed.length > 0) {
          message += `Successfully removed: ${removed.join(', ')}`;
        }
        if (missing.length > 0) {
          message += `\nNot found: ${missing.join(', ')}`;
        }
        toast.success(message);

        setSelectedDocuments([]);
        await fetchDocuments(selectedProfile.uniqueKey);
        await fetchPartners(); // Refresh main list
      } else {
        toast.error(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error('Error removing documents:', error);
      toast.error('Error removing documents. Please try again.');
    } finally {
      setIsManaging(false);
      setManagementOperation('');
    }
  };

  const handleRebuildVectorStore = async () => {
    if (!selectedProfile) return;

    if (!window.confirm('Are you sure you want to rebuild the knowledge base? This may take a few moments.')) {
      return;
    }

    try {
      setIsManaging(true);
      setManagementOperation('Rebuilding knowledge base...');

      const response = await fetch(`${process.env.REACT_APP_RAG_SERVER_URL}/rag-api/users/documents/${selectedProfile.uniqueKey}/rebuild`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          "Authorization": `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`Knowledge base rebuilt successfully! Processed ${result.data.chunksProcessed} chunks from ${result.data.documentsProcessed} documents.`);
      } else {
        toast.error(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error('Error rebuilding vector store:', error);
      toast.error('Error rebuilding knowledge base. Please try again.');
    } finally {
      setIsManaging(false);
      setManagementOperation('');
    }
  };

  const handleNewFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file =>
      file.type === 'application/pdf' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/msword'
    );
    setNewFiles(prev => [...prev, ...validFiles]);
  };

  const handleNewUrlChange = (fileName, url) => {
    setNewFileUrls(prev => ({
      ...prev,
      [fileName]: url
    }));
  };

  const removeNewFile = (index) => {
    const fileToRemove = newFiles[index];
    setNewFiles(newFiles.filter((_, i) => i !== index));
    setNewFileUrls(prev => {
      const updated = { ...prev };
      delete updated[fileToRemove.name];
      return updated;
    });
  };

  const toggleDocumentSelection = (filename) => {
    setSelectedDocuments(prev => {
      if (prev.includes(filename)) {
        return prev.filter(f => f !== filename);
      } else {
        return [...prev, filename];
      }
    });
  };

  const toggleRowExpansion = (userId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(userId)) {
      newExpandedRows.delete(userId);
    } else {
      newExpandedRows.add(userId);
    }
    setExpandedRows(newExpandedRows);
  };

  return (
    <div className="dashboard">
      {/* <div className="sidebar">
        <h2 className="sidebar-title">Navigation</h2>
        <div className="sidebar-section">
          <p className="sidebar-label">Go to</p>
          <div 
            className={`nav-item ${activeTab === 'admin-setup' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin-setup')}
          >
            <span className="nav-dot"></span>
            Admin Setup
          </div>
          <div 
            className={`nav-item ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            <span className="nav-dot"></span>
            Chat
          </div>
        </div>
      </div> */}

      <div className="main-content">
        <div className="header">
          <h1>Partner Management Dashboard</h1>
        </div>

        {activeTab === 'table-view' && (
          <div className="table-view">
            <div className="table-header">
              <div className="table-controls">
                <button className="create-btn" onClick={handleCreateNew}>
                  + Create New Entries
                </button>
                <div className="search-container">
                  <input
                    type="text"
                    className="partner-search-input"
                    placeholder="Search by name, company, department, or key..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <span className="search-icon"></span>
                </div>
              </div>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name(Role)</th>
                    <th>Company Name</th>
                    <th>Department Name</th>
                    <th>Unique Key</th>
                    <th>Files</th>
                    <th>Created Date</th>
                    <th>Last Activity</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingData ? (
                    <tr>
                      <td colSpan="9" className="loading-data">Loading partner data...</td>
                    </tr>
                  ) : filteredUserData.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="no-data">No data found</td>
                    </tr>
                  ) : (
                    filteredUserData.map(user => (
                      <tr key={user.id}>
                        <td><b>{user.userId?.name}</b>({user.userId?.role})<br />
                           <span>{user.userId?.email}</span>
                        </td>
                        <td>{user.companyName}</td>
                        <td>{user.departmentName}</td>
                        <td>
                          <div className="key-cell">
                            <code className="key-display" onClick={() => handleRagKeyClick(user)} title="Click to view analytics">
                              {user.uniqueKey}
                            </code>
                            <span
                              className="copy-key-btn-inline"
                              onClick={() => {
                                navigator.clipboard.writeText(user.uniqueKey);
                                toast.success('Key copied to clipboard!');
                              }}
                              title="Copy key to clipboard"
                            >
                              📋
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="file-section">
                            <button
                              className="expand-files-btn"
                              onClick={() => toggleRowExpansion(user.id)}
                              title={expandedRows.has(user.id) ? 'Hide files' : 'Show files'}
                            >
                              {expandedRows.has(user.id) ? '📁' : '📂'}
                              {user.files ? user.files.length : user.filesUploaded || 0} file{(user.files ? user.files.length : user.filesUploaded || 0) !== 1 ? 's' : ''}
                              {expandedRows.has(user.id) ? ' ▲' : ' ▼'}
                            </button>
                            {expandedRows.has(user.id) && user.files && user.files.length > 0 && (
                              <div className="file-list expanded">
                                {user.files.map((file, index) => (
                                  <span key={index} className="file-tag">{file}</span>
                                ))}
                              </div>
                            )}
                            {expandedRows.has(user.id) && (!user.files || user.files.length === 0) && (
                              <div className="file-list expanded">
                                <span className="no-files">No file details available</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>{user.createdAt}</td>
                        <td>
                          {user.lastDocumentActivity ? (
                            <div className={`activity-badge ${user.lastDocumentActivity.action}`}>
                              <span className="activity-action">
                                {user.lastDocumentActivity.action === 'added' && 'Added'}
                                {user.lastDocumentActivity.action === 'deleted' && 'Deleted'}
                                {user.lastDocumentActivity.action === 'created' && 'Created'}
                              </span>
                              <span className="activity-count">
                                {user.lastDocumentActivity.documentCount} doc{user.lastDocumentActivity.documentCount !== 1 ? 's' : ''}
                              </span>
                              <span className="activity-time">
                                {new Date(user.lastDocumentActivity.timestamp).toLocaleDateString()}
                              </span>
                            </div>
                          ) : (
                            <span className="no-activity">No recent activity</span>
                          )}
                        </td>
                        <td>
                          <span className={user.isActive === true ? "status-badge active" : "status-badge inactive"}>
                            {user.isActive === true ? (
                              <>
                                <FaCheckCircle style={{ marginRight: '6px' }} />
                                Active
                              </>
                            ) : (
                              <>
                                <FaTimesCircle style={{ marginRight: '6px' }} />
                                Inactive
                              </>
                            )}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className={user.isActive === true ? "status-btn active" : "status-btn inactive"}
                              title={user.isActive === true ? "Click to deactivate" : "Click to activate"}
                              onClick={() => handleToggleStatus(user.uniqueKey, user.isActive === true)}
                            >
                              {user.isActive === true ? <FaToggleOn /> : <FaToggleOff />}
                            </button>
                            <button
                              className="manage-btn"
                              title="Manage Documents"
                              onClick={() => handleManageDocuments(user)}
                            >
                              <FaFolder />
                            </button>
                            <button
                              className="delete-btn"
                              title="Delete"
                              onClick={() => handleDeletePartner(user.uniqueKey)}
                            >
                              <FaTrashAlt />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {selectedKey && (
  <div style={styles.modalOverlay}>
    <div style={styles.modalContent}>
      
      {/* CLOSE BUTTON */}
      <button
        style={styles.closeBtn}
        onClick={() => setSelectedKey(null)}
      >
        ✖
      </button>

      {/* YOUR ANALYTICS */}
      <RAGKeyAnalytics ragKey={selectedKey} activeCompany={activeCompany} activeDepartment={activeDepartment} />

    </div>
  </div>
)}
            
          </div>
          
        )}


        {activeTab === 'admin-setup' && (
          <div className="admin-setup">
            <div className="setup-header">
              <button className="back-btn" onClick={handleBackToTable}>
                ← Back to Table
              </button>
              <h2>Partner Admin Setup</h2>
            </div>
            <p className="subtitle">Create a new company/department profile and upload documents.</p>

            <div className="form-section">
              <label htmlFor="company-name">Company Name</label>
              <input
                id="company-name"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="form-input"
                placeholder="Enter company name"
              />
            </div>

            <div className="form-section">
              <label htmlFor="department-name">Department Name</label>
              <input
                id="department-name"
                type="text"
                value={departmentName}
                onChange={(e) => setDepartmentName(e.target.value)}
                className="form-input"
                placeholder="Enter department name"
              />
            </div>

            <div className="form-section">
              <label>Upload PDF and Word documents</label>
              <div
                className={`file-upload-area ${isDragOver ? 'drag-over' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <div className="upload-icon">☁️</div>
                <p>Drag and drop files here</p>
                <p className="file-limit">Limit 200MB per file • PDF, DOCX</p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.docx,.doc"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="file-input"
                />
                <label htmlFor="file-input" className="browse-btn">
                  Browse files
                </label>
              </div>

              {files.length > 0 && (
                <div className="uploaded-files">
                  <h4>Uploaded Files:</h4>
                  {files.map((file, index) => (
                    <div key={index} className="file-item">
                      <div className="file-info">
                        <div className="file-details">
                          <span className="file-name">📄 {file.name}</span>
                          <span className="file-size">{(file.size / 1024).toFixed(1)}KB</span>
                        </div>
                        <button
                          onClick={() => removeFile(index)}
                          className="remove-file-btn"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {files.length > 0 && (
                <div className="url-section">
                  <h4>Please provide the reference URL for each document:</h4>
                  {files.map((file, index) => (
                    <div key={index} className="url-input-group">
                      <label htmlFor={`url-${index}`}>URL for {file.name}</label>
                      <input
                        id={`url-${index}`}
                        type="url"
                        value={fileUrls[file.name] || ''}
                        onChange={(e) => handleUrlChange(file.name, e.target.value)}
                        className="form-input url-input"
                        placeholder="Enter reference URL for this document"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              className="create-profile-btn"
              onClick={handleCreateProfile}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="loading-content">
                  <div className="adminspinner"></div>
                  <span>Creating Profile...</span>
                </div>
              ) : (
                'Create Profile and Generate Key'
              )}
            </button>

            {isLoading && (
              <div className="loading-overlay">
                <div className="loading-modal">
                  <div className="loading-adminspinner">
                    <div className="adminspinner-ring"></div>
                  </div>
                  <h3>Processing documents and building knowledge base...</h3>
                  <p className="loading-stage">{loadingStage}</p>
                  <p className="loading-message">This may take a moment.</p>
                </div>
              </div>
            )}

            {showKeyModal && (
              <div className="key-modal-overlay">
                <div className="key-modal">
                  <div className="key-modal-header">
                    <h3>Profile created successfully!</h3>
                    <button className="close-modal-btn" onClick={handleCloseModal}>✕</button>
                  </div>
                  <div className="key-modal-content">
                    <p className="key-label">Your unique 16-character key is:</p>
                    <div className="key-display">
                      <code className="generated-key">{generatedKey}</code>
                      <button className="copy-key-btn" onClick={handleCopyKey} title="Copy to clipboard">
                        📋
                      </button>
                    </div>
                    <div className="key-warning">
                      <p className="warning-text">
                        ⚠️ Please save this key. It will be required for the chat interface.
                      </p>
                    </div>
                    <button className="modal-action-btn" onClick={handleCloseModal}>
                      Got it!
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'manage-documents' && selectedProfile && (
          <div className="manage-documents">
            <div className="setup-header">
              <button className="back-btn" onClick={handleBackToTable}>
                ← Back to Table
              </button>
              <h2>Manage Documents</h2>
              <p className="selected-profile">
                {selectedProfile.companyName} - {selectedProfile.departmentName}
                <code className="profile-key">{selectedProfile.uniqueKey}</code>
              </p>
            </div>

            {isManaging && (
              <div className="loading-overlay">
                <div className="loading-modal">
                  <div className="loading-adminspinner">
                    <div className="adminspinner-ring"></div>
                  </div>
                  <h3>{managementOperation}</h3>
                  <p className="loading-message">Please wait...</p>
                </div>
              </div>
            )}

            <div className="document-management-container">
              {/* Current Documents Section */}
              <div className="current-documents-section">
                <h3>Current Documents</h3>
                {isLoadingDocuments ? (
                  <div className="loading-documents">Loading documents...</div>
                ) : (
                  <div className="documents-list">
                    {profileDocuments.length === 0 ? (
                      <p className="no-documents">No documents found for this profile.</p>
                    ) : (
                      <>
                        <div className="documents-controls">
                          <button
                            className="rebuild-btn"
                            onClick={handleRebuildVectorStore}
                            disabled={isManaging}
                          >
                            🔄 Rebuild Knowledge Base
                          </button>
                          <button
                            className="remove-selected-btn"
                            onClick={handleRemoveDocuments}
                            disabled={selectedDocuments.length === 0 || isManaging}
                          >
                            🗑️ Remove Selected ({selectedDocuments.length})
                          </button>
                        </div>
                        <div className="documents-grid">
                          {profileDocuments.map((doc, index) => (
                            <div key={index} className={`document-item ${selectedDocuments.includes(doc.name) ? 'selected' : ''}`}>
                              <div className="document-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedDocuments.includes(doc.name)}
                                  onChange={() => toggleDocumentSelection(doc.name)}
                                />
                              </div>
                              <div className="document-info">
                                <h4 className="document-name">📄 {doc.name}</h4>
                                <p className="document-size">{doc.size_kb} KB</p>
                                <p className="document-url">
                                  {doc.url ? (
                                    <a href={doc.url} target="_blank" rel="noopener noreferrer">🔗 View Source</a>
                                  ) : (
                                    <span className="no-url">ℹ️ No URL provided</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Add Documents Section */}
              <div className="add-documents-section">
                <h3>Add New Documents</h3>
                <div className="form-section">
                  <label>Upload PDF and Word documents</label>
                  <div className="file-upload-area">
                    <div className="upload-icon">☁️</div>
                    <p>Drag and drop files here or click to browse</p>
                    <p className="file-limit">Limit 200MB per file • PDF, DOCX</p>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.docx,.doc"
                      onChange={handleNewFileSelect}
                      style={{ display: 'none' }}
                      id="new-file-input"
                    />
                    <label htmlFor="new-file-input" className="browse-btn">
                      Browse files
                    </label>
                  </div>

                  {newFiles.length > 0 && (
                    <div className="uploaded-files">
                      <h4>New Files to Add:</h4>
                      {newFiles.map((file, index) => (
                        <div key={index} className="file-item">
                          <div className="file-info">
                            <div className="file-details">
                              <span className="file-name">📄 {file.name}</span>
                              <span className="file-size">{(file.size / 1024).toFixed(1)}KB</span>
                            </div>
                            <button
                              onClick={() => removeNewFile(index)}
                              className="remove-file-btn"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {newFiles.length > 0 && (
                    <div className="url-section">
                      <h4>Please provide the reference URL for each document:</h4>
                      {newFiles.map((file, index) => (
                        <div key={index} className="url-input-group">
                          <label htmlFor={`new-url-${index}`}>URL for {file.name}</label>
                          <input
                            id={`new-url-${index}`}
                            type="url"
                            value={newFileUrls[file.name] || ''}
                            onChange={(e) => handleNewUrlChange(file.name, e.target.value)}
                            className="form-input url-input"
                            placeholder="Enter reference URL for this document"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    className="add-documents-btn"
                    onClick={handleAddDocuments}
                    disabled={newFiles.length === 0 || isManaging}
                  >
                    {isManaging ? (
                      <div className="loading-content">
                        <div className="adminspinner"></div>
                        <span>Adding Documents...</span>
                      </div>
                    ) : (
                      `Add ${newFiles.length} Document(s)`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* {activeTab === 'chat' && (
          <Chat />
        )} */}
      </div>
    </div>
  );
};

export default PartnerDashboard;
